import React, { useContext } from 'react'
import { Form, FormItemProps, Radio, Space } from 'antd'
import { ColorPicker } from '../../../../../components/color-picker/color-picker'
import { FileUploader, Well } from '@pushly/aqe/lib/components'
import { simpleNotification } from '../../../../../_utils/utils'
import {
    TemplatizedTextFieldWithColor,
    TextWithColorValues,
    TextWithForegroundColorValue,
} from '../../../../../components/templatized-text-field-with-color/templatized-text-field-with-color'
import { PromptButtonConfig, PromptDevicePosition, SlidePromptTheme } from '@pushly/models/lib/structs'
import { PromptTextConfig } from '@pushly/models/lib/structs/prompts/prompt-text-config'
import {
    DEFAULT_ALLOW_BUTTON_COLOR,
    DEFAULT_ALLOW_TEXT,
    DEFAULT_ALLOW_TEXT_COLOR,
    DEFAULT_BACKGROUND_COLOR,
    DEFAULT_DESKTOP_POSITION,
    DEFAULT_DISMISS_BUTTON_COLOR,
    DEFAULT_DISMISS_TEXT,
    DEFAULT_DISMISS_TEXT_COLOR,
    DEFAULT_LAYOUT,
    DEFAULT_MOBILE_POSITION,
    DEFAULT_SUBTITLE_COLOR,
    DEFAULT_TITLE_COLOR,
} from '../../constants'
import { PromptEditorContext } from '../../context'

import './slide-prompt-theme-editor.scss'

// Uses props to guarantee a specific style of Prompt
type SlidePromptDataFormProps = {
    value?: SlidePromptTheme
    onChange: (theme: SlidePromptTheme) => void
}

export const SlidePromptThemeEditor: React.FC<SlidePromptDataFormProps> = ({ value, onChange }) => {
    const { loading, domain } = useContext(PromptEditorContext)

    const theme = value ?? new SlidePromptTheme()

    const setTheme = (setter: (theme: SlidePromptTheme) => SlidePromptTheme) => {
        onChange(setter(theme.clone()))
    }

    const radioFormLayout: FormItemProps = {
        labelCol: { span: 5 },
        wrapperCol: { span: 19 },
        labelAlign: 'left',
    }

    return (
        <Well className="customizations-well" title="Theme" hideFooter={true} mode="ghost" flush>
            <Form layout="vertical" colon={false}>
                <Form.Item {...radioFormLayout} label="Slide Layout" layout="horizontal">
                    {!loading && (
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            value={theme.layout ?? DEFAULT_LAYOUT}
                            onChange={(ev) => {
                                setTheme((update) => {
                                    update.layout = ev.target.value
                                    return update
                                })
                            }}
                            options={[
                                { value: 'normal', label: 'Horizontal' },
                                { value: 'stacked', label: 'Vertical' },
                            ]}
                            optionType="button"
                        />
                    )}
                </Form.Item>

                <Form.Item {...radioFormLayout} label="Desktop Position" layout="horizontal">
                    {!loading && (
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            value={theme.position.desktop ?? DEFAULT_DESKTOP_POSITION}
                            onChange={(ev) => {
                                setTheme((update) => {
                                    update.position = PromptDevicePosition.from({
                                        desktop: ev.target.value,
                                        mobile: update.position?.mobile ?? DEFAULT_MOBILE_POSITION,
                                    })
                                    return update
                                })
                            }}
                            options={[
                                { value: 'top', label: 'Top' },
                                { value: 'bottom', label: 'Bottom' },
                            ]}
                            optionType="button"
                        />
                    )}
                </Form.Item>

                <Form.Item {...radioFormLayout} label="Mobile Position" layout="horizontal">
                    {!loading && (
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            value={theme.position.mobile ?? DEFAULT_MOBILE_POSITION}
                            onChange={(ev) => {
                                setTheme((update) => {
                                    update.position = PromptDevicePosition.from({
                                        mobile: ev.target.value,
                                        desktop: update.position?.desktop ?? DEFAULT_DESKTOP_POSITION,
                                    })
                                    return update
                                })
                            }}
                            options={[
                                { value: 'top', label: 'Top' },
                                { value: 'bottom', label: 'Bottom' },
                            ]}
                            optionType="button"
                        />
                    )}
                </Form.Item>

                <TemplatizedTextFieldWithColor<TextWithForegroundColorValue>
                    label="Title"
                    placeholder="Title"
                    hideMacros={true}
                    value={
                        !prompt
                            ? undefined
                            : {
                                  value: theme.title?.text,
                                  fgColor: theme.title?.textColor ?? DEFAULT_TITLE_COLOR,
                              }
                    }
                    onChange={(nextValue) => {
                        setTheme((update) => {
                            update.title = PromptTextConfig.from({
                                text: nextValue.value!,
                                textColor: nextValue.fgColor!,
                            })
                            return update
                        })
                    }}
                    formItemProps={{ layout: 'vertical' }}
                />

                <TemplatizedTextFieldWithColor<TextWithForegroundColorValue>
                    label="Subtitle"
                    placeholder="Subtitle"
                    hideMacros={true}
                    value={
                        !prompt
                            ? undefined
                            : {
                                  value: theme.subtitle?.text,
                                  fgColor: theme.subtitle?.textColor ?? DEFAULT_SUBTITLE_COLOR,
                              }
                    }
                    onChange={(nextValue) => {
                        setTheme((update) => {
                            update.subtitle = PromptTextConfig.from({
                                text: nextValue.value!,
                                textColor: nextValue.fgColor!,
                            })
                            return update
                        })
                    }}
                    formItemProps={{ layout: 'vertical' }}
                />
                <TemplatizedTextFieldWithColor<TextWithColorValues>
                    label="Allow Button"
                    placeholder="Allow"
                    hideMacros={true}
                    showBgColorOption={true}
                    value={
                        !prompt
                            ? undefined
                            : {
                                  value: theme.allowButton?.text ?? DEFAULT_ALLOW_TEXT,
                                  fgColor: theme.allowButton?.textColor ?? DEFAULT_ALLOW_TEXT_COLOR,
                                  bgColor: theme.allowButton?.backgroundColor ?? DEFAULT_ALLOW_BUTTON_COLOR,
                              }
                    }
                    onChange={(nextValue) => {
                        setTheme((update) => {
                            update.allowButton = PromptButtonConfig.from({
                                text: nextValue.value!,
                                textColor: nextValue.fgColor!,
                                backgroundColor: nextValue.bgColor!,
                            })
                            return update
                        })
                    }}
                    formItemProps={{ layout: 'vertical' }}
                />

                <TemplatizedTextFieldWithColor<TextWithColorValues>
                    label="Dismiss Button"
                    placeholder="Dismiss"
                    hideMacros={true}
                    showBgColorOption={true}
                    value={
                        !prompt
                            ? undefined
                            : ({
                                  value: theme.dismissButton?.text ?? DEFAULT_DISMISS_TEXT,
                                  fgColor: theme.dismissButton?.textColor ?? DEFAULT_DISMISS_TEXT_COLOR,
                                  bgColor: theme.dismissButton?.backgroundColor ?? DEFAULT_DISMISS_BUTTON_COLOR,
                              } satisfies TextWithColorValues)
                    }
                    onChange={(nextValue) => {
                        setTheme((update) => {
                            update.dismissButton = PromptButtonConfig.from({
                                text: nextValue.value!,
                                textColor: nextValue.fgColor!,
                                backgroundColor: nextValue.bgColor!,
                            })
                            return update
                        })
                    }}
                    formItemProps={{ layout: 'vertical' }}
                />

                <Space align="start">
                    <Form.Item layout="vertical" label="Background Color">
                        {!loading && (
                            <ColorPicker.Button
                                className="prompt-background-picker"
                                value={theme.backgroundColor ?? DEFAULT_BACKGROUND_COLOR}
                                onChange={(nextValue: string) => {
                                    setTheme((update) => {
                                        update.backgroundColor = nextValue
                                        return update
                                    })
                                }}
                            />
                        )}
                    </Form.Item>

                    <Form.Item layout="vertical" label="Image">
                        {!loading && (
                            <FileUploader
                                className="image-upload"
                                type="image"
                                ownerType="domain"
                                ownerId={domain.id}
                                imageConfig={{ quality: domain.mediaQuality }}
                                value={!theme.image ? undefined : { url: theme.image }}
                                onSuccess={(img) => {
                                    setTheme((update) => {
                                        update.image = img.url
                                        return update
                                    })
                                }}
                                onRemove={() => {
                                    setTheme((update) => {
                                        update.image = null!
                                        return update
                                    })
                                }}
                                onError={(res: any) => {
                                    const error = res?.error
                                    if (error && error.error_type === 'invalid_file_type_error') {
                                        return simpleNotification('error', error.message)
                                    }
                                }}
                            />
                        )}
                    </Form.Item>
                </Space>
            </Form>
        </Well>
    )
}
